import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Plus, Edit2, Trash2, Layout } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loading } from '../../components/Loading';
import { ImageUploader } from '../../components/ImageUploader';
import { getBanners, addBanner, updateBanner, deleteBanner } from '../../utils/firestoreService';

export const AdminBanners = () => {
  const { user } = useAuth();
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [formData, setFormData] = useState({
    alt: '',
    imageUrl: '',
    order: 0,
  });

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    setLoading(true);
    try {
      const data = await getBanners();
      setBanners(data);
    } catch (error) {
      console.error('Error fetching banners:', error);
      toast.error('Error al cargar los banners');
      setBanners([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Debes estar autenticado');
      return;
    }

    if (!formData.imageUrl) {
      toast.error('Debes subir una imagen para el banner');
      return;
    }

    try {
      const submitData = {
        ...formData,
        order: parseInt(formData.order) || 0,
      };

      if (editingBanner) {
        await updateBanner(editingBanner.id, submitData);
        toast.success('Banner actualizado correctamente');
      } else {
        await addBanner(submitData);
        toast.success('Banner creado correctamente');
      }

      setDialogOpen(false);
      resetForm();
      fetchBanners();
    } catch (error) {
      console.error('Error saving banner:', error);
      toast.error('Error al guardar el banner. Verifica las reglas de Firestore.');
    }
  };

  const handleDelete = async (bannerId) => {
    if (!user) {
      toast.error('Debes estar autenticado');
      return;
    }

    if (!window.confirm('¿Estás seguro de eliminar este banner?')) return;

    try {
      await deleteBanner(bannerId);
      toast.success('Banner eliminado correctamente');
      fetchBanners();
    } catch (error) {
      console.error('Error deleting banner:', error);
      toast.error('Error al eliminar el banner');
    }
  };

  const openEditDialog = (banner) => {
    setEditingBanner(banner);
    setFormData({
      alt: banner.alt || '',
      imageUrl: banner.imageUrl || '',
      order: banner.order || 0,
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingBanner(null);
    setFormData({
      alt: '',
      imageUrl: '',
      order: 0,
    });
  };

  if (loading) return <Loading />;

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-4xl font-fredoka font-bold text-[#1A1A2E]" data-testid="admin-banners-title">
          Banners de Inicio
        </h1>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <button
              className="bg-[#00C2CB] text-white px-6 py-3 rounded-full font-fredoka font-bold hover:bg-[#00C2CB]/90 transition-all flex items-center gap-2"
              data-testid="add-banner-button"
            >
              <Plus className="w-5 h-5" />
              Nuevo Banner
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-fredoka text-2xl">
                {editingBanner ? 'Editar Banner' : 'Nuevo Banner'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="alt">Descripción / Texto Alt *</Label>
                <Input
                  id="alt"
                  value={formData.alt}
                  onChange={(e) => setFormData({ ...formData, alt: e.target.value })}
                  required
                  placeholder="Ej. Promoción día del niño"
                  data-testid="banner-alt-input"
                />
              </div>
              <div>
                <Label htmlFor="order">Orden (Número) *</Label>
                <Input
                  id="order"
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: e.target.value })}
                  required
                  data-testid="banner-order-input"
                />
                <p className="text-xs text-[#9CA3AF] mt-1 font-quicksand">
                  Los banners se ordenarán de menor a mayor.
                </p>
              </div>
              <div>
                <Label htmlFor="imageUrl">Imagen del Banner *</Label>
                <ImageUploader
                  currentImage={formData.imageUrl}
                  onImageUploaded={(url) => setFormData({ ...formData, imageUrl: url })}
                />
                <p className="text-xs text-[#9CA3AF] mt-2 font-quicksand">
                  La imagen se subirá a Firebase Storage
                </p>
              </div>
              <button
                type="submit"
                className="w-full bg-[#FF6B00] text-white px-6 py-3 rounded-full font-fredoka font-bold hover:bg-[#FF6B00]/90 transition-all"
                data-testid="banner-submit-button"
              >
                {editingBanner ? 'Actualizar' : 'Crear'} Banner
              </button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {banners.length === 0 && !loading ? (
          <div className="col-span-full text-center py-20 px-4 bg-white rounded-2xl shadow-lg">
            <Layout className="w-16 h-16 mx-auto mb-4 text-[#9CA3AF]" />
            <h3 className="text-xl font-fredoka font-bold text-[#1A1A2E] mb-2">
              No hay banners aún
            </h3>
            <p className="text-[#4A4A6A] font-quicksand mb-6">
              Agrega imágenes llamativas para el carrusel de la página de inicio.
            </p>
            <button
              onClick={() => setDialogOpen(true)}
              className="bg-[#00C2CB] text-white px-6 py-3 rounded-full font-fredoka font-bold hover:bg-[#00C2CB]/90 transition-all"
              data-testid="empty-state-add-banner"
            >
              Agregar Primer Banner
            </button>
          </div>
        ) : (
          banners.map((banner) => (
            <div
              key={banner.id}
              className="bg-white rounded-2xl p-6 shadow-lg flex flex-col relative group"
              data-testid={`banner-card-${banner.id}`}
            >
              <div className="absolute top-4 right-4 bg-slate-100/90 backdrop-blur text-slate-800 text-xs font-bold px-3 py-1 rounded-md z-10">
                Orden: {banner.order}
              </div>
              <div className="w-full aspect-[16/9] rounded-xl overflow-hidden bg-slate-100 mb-4 relative">
                {banner.imageUrl ? (
                  <img src={banner.imageUrl} alt={banner.alt} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="text-slate-400 font-quicksand">Sin imagen</span>
                  </div>
                )}
              </div>
              <h3 className="font-fredoka font-bold text-lg text-[#1A1A2E] mb-4">{banner.alt}</h3>
              <div className="flex gap-4 mt-auto">
                <button
                  onClick={() => openEditDialog(banner)}
                  className="flex-1 px-4 py-2 text-[#00C2CB] border border-[#00C2CB] hover:bg-[#00C2CB]/10 rounded-full font-quicksand font-semibold transition-all text-sm flex items-center justify-center gap-1"
                  data-testid={`edit-banner-${banner.id}`}
                >
                  <Edit2 className="w-4 h-4" />
                  Editar
                </button>
                <button
                  onClick={() => handleDelete(banner.id)}
                  className="flex-1 px-4 py-2 text-red-600 border border-red-600 hover:bg-red-50 rounded-full font-quicksand font-semibold transition-all text-sm flex items-center justify-center gap-1"
                  data-testid={`delete-banner-${banner.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                  Borrar
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
